import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import moment from "moment";
import Barcode from "react-barcode";

export default function AladiCoupon({ couponData }) {
    if (!couponData) return null;
    const { Outlet, Slot, Paid, Status, Code, createdAt, ReferenceID } = couponData;

    const getPaymentStatus = () => {
        if (Status === 0 && Paid === 0) return "Payment Pending";
        if (Status === 0 && Paid === 1) return "Payment Failed";
        if (Status === 1 && Paid === 1) return "Active";
        if (Status === 2) return "Redeemed";
        return new Date(createdAt) < new Date() ? "Expired" : "Unknown";
    };

    const paymentStatus = getPaymentStatus();

    // Show simplified view for pending/failed coupons
    if (paymentStatus === "Payment Pending" || paymentStatus === "Payment Failed") {
        return (
            <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md mx-auto my-6 p-6 rounded-2xl shadow-lg border border-gray-300 bg-white text-center"
            >
                <h3 className="text-lg font-semibold text-red-600">
                    {paymentStatus}
                </h3>
                <p className="mt-2 text-gray-700 text-sm">
                    Reference ID: <span className="font-mono">{ReferenceID || Code}</span>
                </p>
                <p className="mt-4 text-gray-600 text-sm">
                    Please contact <a href="mailto:support@halwakadai.com" className="text-blue-600 underline">support</a> for assistance.
                </p>
            </motion.section>
        );
    }

    // Show full coupon for successful/active coupons
    return (
        <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto my-6 rounded-[2rem] overflow-hidden shadow-lg border border-yellow-300
                 bg-gradient-to-r from-yellow-500 via-yellow-400 to-rose-700
                 grid md:grid-cols-2"
        >
            {/* Left – Mustard section */}
            <div className="bg-yellow-500 p-6 relative">
                <img
                    src={require("../../assets/yellow_bg_logo.png")}
                    alt="Aaladipattiyan Logo"
                    className="h-20 w-auto object-contain"
                />

                {/* Coupon / White content */}
                <div
                    className="mt-1 w-72 bg-white shadow text-center p-3 flex items-center rounded-2xl relative
               before:absolute before:inset-0 before:-z-10 
               before:mask-image-[radial-gradient(circle,transparent_10px,black_11px)]
               before:mask-repeat repeat-y before:mask-size-[20px_40px]
               before:bg-yellow-500"
                >
                    <div className="flex-1 px-2">
                        <ul className="text-rose-900 text-[10px] font-medium space-y-0.5 leading-tight">
                            <li>Srivillipuththur Palkova</li>
                            <li>Kaju katli</li>
                            <li>Athirasam</li>
                            <li>Badam Soan papdi</li>
                            <li>Ghee laddu</li>
                            <li>Mothi laddu</li>
                            <li>Spl laddu</li>
                            <li>Aaladi Mysore pak</li>
                            <li>Ghee Mysore pak</li>
                            <li>Nattusakkarai badusha</li>
                            <li>Ghee murukku</li>
                            <li>Rose bites</li>
                            <li>Assorted bites</li>
                            <li className="text-blue-700 font-semibold">
                                + Free Tirunelveli Ghee Halwa
                            </li>
                        </ul>
                    </div>
                    <div className="ml-3 flex items-stretch">
                        <img
                            src={require("../../assets/Treat_box_2.jpg")}
                            alt="Product"
                            className="h-full w-auto object-cover rounded-xl"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-4">
                    <span className="block text-xs text-gray-700">
                        Generated: {moment(createdAt).format("DD-MM-YYYY")}
                    </span>
                    <span className="block mt-1 text-sm font-semibold">
                        Status: {paymentStatus}
                    </span>
                </div>

                {Status === 1 && Paid === 1 && Code && (
                    <div className="mt-4 bg-white rounded-xl p-2">
                        <Barcode value={Code} format="CODE128" height={50} width={1.2} />
                    </div>
                )}
            </div>

            {/* Right – Red offer section */}
            <div className="bg-rose-700 text-white p-6 flex flex-col justify-between">
                <div>
                    <div className="text-center">
                        <p className="text-3xl font-extrabold">
                            FREE ¼ KILO <br /> TIRUNELVELI GHEE HALWA
                        </p>
                    </div>

                    <h3 className="mt-4 text-lg font-semibold">
                        {Slot?.dates}
                    </h3>
                    <div className="mt-2 inline-block bg-yellow-400 text-rose-900 px-3 py-1 rounded-full font-bold">
                        {Outlet}
                    </div>

                    <p className="mt-4 text-yellow-200 text-sm leading-relaxed">
                        Pay only ₹449 during pickup and get Festive Sweets Combo worth ₹550
                        and ₹130 worth Tirunelveli Halwa FREE!!!
                    </p>

                    <div className="flex-1 px-2 mt-4 text-yellow-200 text-sm leading-relaxed">
                        <ul className="text-yellow-200 text-[12px] font-medium space-y-0.5 list-disc list-inside leading-tight">
                            <li>கூப்பன் 16 அக்டோபர் 2025 முதல் 20 அக்டோபர் 2025 வரை மட்டுமே செல்லுபடியாகும்.</li>
                            <li>இந்த கூப்பனை ஆன்லைனில் பயன்படுத்த முடியாது.</li>
                            <li>தேர்ந்தெடுத்த கடையிலும், தேர்ந்தெடுத்த நேரத்திலும் மட்டுமே இந்த கூப்பனை பயன்படுத்த முடியும்.</li>
                            <li>இந்த கூப்பன் பணத்தைத் திருப்பித் தர இயலாது மற்றும் மாற்றிக்கொள்ள இயலாது.</li>
                            <li>கூப்பன் ரீடீம் செய்யும் போது இறுதி பில் தொகையில் ஜிஎஸ்டி (GST) வசூலிக்கப்படும்.</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-6 text-center">
                    <div className="inline-block bg-white text-rose-700 px-4 py-1 rounded-full font-bold">
                        {Paid ? "Paid ₹99" : "Pending"}
                    </div>
                </div>
            </div>
        </motion.section>
    );
}

AladiCoupon.propTypes = { couponData: PropTypes.object.isRequired };
